import React, { useCallback, useEffect, useState } from 'react';
import { AlertCircle, Plus, RefreshCw, Trash2, KeyRound } from 'lucide-react';
import uapiAccessService from '../services/UapiAccessService';
import entityMktSettingsService from '../services/partnerMktSettingsService';
import marketplacesettingsService from '../services/marketplacesettingsService';
import Button from './shared/Button';
import Spinner from './shared/Spinner';
import CopyButton from './shared/CopyButton';
import ConfirmModal from './shared/ConfirmModal';
import CredentialSelector from './shared/CredentialSelector';
import { formatDateTime } from '../utils/datetime';
import './UapiAccessTab.css';

// Universal API access, on a hotel's page and on chain settings.
//
// ONE COMPONENT FOR BOTH, told apart by `entityType`. A chain sees its whole estate — which properties
// are provisioned and which are not, which is the question a chain actually asks — and a hotel sees its
// own. Two components would be two lists of the same rows drifting apart, and the chain view is the
// hotel view with one more column.
//
// WHAT PROVISIONING IS, in the order it happens:
//   1. the property's INBOUND credential — what a caller presents to us. One per hotel; issued here if
//      it has none, through the same endpoints the Credentials tab uses.
//   2. the BINDING — which backend system this property is routed to, with which of the property's own
//      credentials, and the x-hotelid that names the pair.
// The secret is shown ONCE, on issue. It is not stored anywhere it can be read back, which is the point
// of a secret; the screen says so rather than letting somebody discover it later.
//
// WHAT IS NOT HERE: module grants. They decide what a credential is entitled to CALL, and that is not a
// decision the holder of a credential gets to make about itself — it stays with the operator, on
// Universal API › Authentication and Access.

const GENERATED_SETTINGS = (issued) => ({
    CLIENTID: issued.clientId,
    CLIENTSECRET: issued.clientSecret,
    TOKENURL: issued.tokenUrl,
    SCOPE: issued.scope,
    APPKEY: issued.appKey,
    ENTERPRISEID: issued.enterpriseId,
    EXTERNALSYSTEM: issued.externalSystem,
});

const errText = (e, fallback) =>
    e?.response?.data?.error?.message || e?.response?.data?.message || e?.message || fallback;

const UapiAccessTab = ({ entityType, entityId, accountId }) => {
    const isChain = entityType === 'account';

    const [rows, setRows] = useState([]);
    const [systems, setSystems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // The add form, opened per hotel. On a chain page the hotel is whichever row's button was pressed;
    // on a hotel page there is only one.
    const [adding, setAdding] = useState(null);      // { hotelId, hotelname }
    const [marketplaceId, setMarketplaceId] = useState('');
    const [credential, setCredential] = useState(null);
    const [hotelIdOverride, setHotelIdOverride] = useState('');
    const [label, setLabel] = useState('');
    const [saving, setSaving] = useState(false);

    // Shown once, never fetched back.
    const [issued, setIssued] = useState(null);
    const [confirmRemove, setConfirmRemove] = useState(null);

    const load = useCallback(async () => {
        if (!entityId) { setRows([]); return; }
        try {
            setLoading(true);
            setError(null);
            const res = await uapiAccessService.list(
                isChain ? { accountId: entityId } : { hotelId: entityId });
            setRows((res?.data || res)?.access || []);
        } catch (e) {
            setError(errText(e, 'Could not load Universal API access'));
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [entityId, isChain]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        uapiAccessService.systems()
            .then(r => setSystems((r?.data || r)?.systems || []))
            .catch(() => setSystems([]));
    }, []);

    const openAdd = (hotelId, hotelname) => {
        setAdding({ hotelId, hotelname });
        setMarketplaceId('');
        setCredential(null);
        setHotelIdOverride('');
        setLabel('');
        setError(null);
    };

    const save = async () => {
        if (!marketplaceId) { setError('Choose a backend system'); return; }
        if (!credential) { setError('Choose the credential the Universal API should call it with'); return; }
        setSaving(true);
        setError(null);
        try {
            // 1. The inbound credential, only if this hotel has none. Minted FIRST: if the binding then
            //    fails the property is left with a credential and no route, which is recoverable by
            //    doing this again — the reverse would leave a route pointing at nothing.
            const existing = rows.find(r => String(r.hotel_id ?? r.hotelid) === String(adding.hotelId)
                && r.uapi_credential_id);
            let inboundIssued = null;
            if (!existing) {
                const uapiMarketplace = (systems || []).find(s => s.system_key === 'HCONNECTUNIVERSALAPI');
                const uapiMarketplaceId = uapiMarketplace?.id
                    || (await marketplacesettingsService.getSchema({}))?.uapiMarketplaceId;
                const credentialName = `${adding.hotelname} Universal API`;
                const gen = await entityMktSettingsService.generateUapiCredentials({ credentialName });
                inboundIssued = gen.data || gen;
                const schemaRes = await marketplacesettingsService.getSchema({ MARKETPLACEID: uapiMarketplaceId });
                const values = GENERATED_SETTINGS(inboundIssued);
                const settings = (schemaRes.settings || [])
                    .filter(s => values[s.SETTINGNAME] !== undefined && values[s.SETTINGNAME] !== null)
                    .map(s => ({ name: s.SETTINGNAME, value: values[s.SETTINGNAME] }));
                await entityMktSettingsService.saveCredentials(
                    'hotel', adding.hotelId, uapiMarketplaceId, credentialName, settings);
            }

            // 2. The binding. The x-hotelid comes back from the server, which mints it.
            await uapiAccessService.create({
                hotelId: adding.hotelId,
                marketplaceId: Number(marketplaceId),
                credentialId: credential.credential_id || credential.id,
                hotelidOverride: hotelIdOverride.trim() || null,
                label: label.trim() || null,
            });

            if (inboundIssued) setIssued({ ...inboundIssued, hotelname: adding.hotelname });
            setAdding(null);
            await load();
        } catch (e) {
            setError(errText(e, 'Could not provision Universal API access'));
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        const row = confirmRemove;
        setConfirmRemove(null);
        try {
            await uapiAccessService.remove(row.id);
            await load();
        } catch (e) {
            setError(errText(e, 'Could not remove that access'));
        }
    };

    // On a chain, a hotel with no binding still appears — a LEFT JOIN row with a null id. That is the
    // useful half of the estate view and must not be rendered as a broken binding.
    const provisioned = rows.filter(r => r.id);

    return (
        <div className="uat-wrap">
            <div className="uat-head">
                <div>
                    <h3 className="uat-title">Universal API</h3>
                    <p className="uat-subtitle">
                        One interface to every PMS. A property is reachable once it holds a credential and
                        is routed to a backend system; the <code>x-hotelid</code> below is what a caller
                        sends to reach it.
                    </p>
                </div>
                <div className="uat-actions">
                    {!isChain && (
                        <Button variant="secondary" onClick={() => openAdd(entityId, '')} disabled={loading}>
                            <Plus className="icon" /> Add access
                        </Button>
                    )}
                    <Button variant="success" onClick={load} disabled={loading}>
                        <RefreshCw className="icon" /> Refresh
                    </Button>
                </div>
            </div>

            {error && (
                <div className="error-alert">
                    <div className="error-alert-content">
                        <AlertCircle className="error-alert-icon" />
                        <p className="error-alert-message">{error}</p>
                    </div>
                </div>
            )}

            {/* THE SECRET, ONCE. Nothing can read it back, so it is stated plainly rather than left for
                somebody to discover when they need it. */}
            {issued && (
                <div className="uat-issued">
                    <h4><KeyRound size={15} /> Credential issued{issued.hotelname ? ` for ${issued.hotelname}` : ''}</h4>
                    <p className="uat-issued-warn">
                        The secret is shown <strong>once</strong> and is not stored anywhere it can be read
                        back. Copy it now.
                    </p>
                    <div className="uat-issued-row">
                        <span>Client ID</span>
                        <code>{issued.clientId}</code>
                        <CopyButton value={issued.clientId} label="Copy" className="copy-btn-inline" />
                    </div>
                    <div className="uat-issued-row">
                        <span>Client secret</span>
                        <code>{issued.clientSecret}</code>
                        <CopyButton value={issued.clientSecret} label="Copy" className="copy-btn-inline" />
                    </div>
                    {issued.tokenUrl && (
                        <div className="uat-issued-row">
                            <span>Token URL</span>
                            <code>{issued.tokenUrl}</code>
                            <CopyButton value={issued.tokenUrl} label="Copy" className="copy-btn-inline" />
                        </div>
                    )}
                    <Button variant="secondary" onClick={() => setIssued(null)}>Done</Button>
                </div>
            )}

            {loading ? <Spinner /> : (
                <table className="uat-table">
                    <thead>
                        <tr>
                            {isChain && <th>Hotel</th>}
                            <th>Backend system</th>
                            <th>Credential</th>
                            <th>Property code</th>
                            <th>x-hotelid</th>
                            <th>Added</th>
                            <th aria-label="Actions" />
                        </tr>
                    </thead>
                    <tbody>
                        {provisioned.map(r => (
                            <tr key={r.id}>
                                {isChain && <td>{r.hotelname}</td>}
                                <td>
                                    {r.label || r.system_name}
                                    {r.label && <div className="uat-sub">{r.system_name}</div>}
                                </td>
                                <td>{r.credential_name || '—'}</td>
                                <td>{r.hotelid_override || <span className="uat-sub">on the credential</span>}</td>
                                <td>
                                    <div className="uat-xhotel">
                                        <code>{r.uapi_hotel_id}</code>
                                        <CopyButton value={r.uapi_hotel_id} label="Copy" className="copy-btn-inline" />
                                    </div>
                                </td>
                                <td>{r.created_at ? formatDateTime(r.created_at) : '—'}</td>
                                <td>
                                    <button
                                        type="button"
                                        className="uat-icon-btn"
                                        onClick={() => setConfirmRemove(r)}
                                        title="Remove this access"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </td>
                            </tr>
                        ))}

                        {/* On a chain, the hotels with nothing — the half of the answer a list of
                            bindings cannot give. */}
                        {isChain && rows.filter(r => !r.id).map(r => (
                            <tr key={`none-${r.hotelid}`} className="uat-row-none">
                                <td>{r.hotelname}</td>
                                <td colSpan={5}>Not provisioned for the Universal API</td>
                                <td>
                                    <Button variant="secondary" onClick={() => openAdd(r.hotelid, r.hotelname)}>
                                        <Plus className="icon" /> Add
                                    </Button>
                                </td>
                            </tr>
                        ))}

                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={isChain ? 7 : 6} className="uat-empty">
                                    Nothing is provisioned for the Universal API yet.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            )}

            {adding && (
                <ConfirmModal
                    title={`Add Universal API access${adding.hotelname ? ` — ${adding.hotelname}` : ''}`}
                    message={'Choose the backend system this property is routed to and the credential the '
                        + 'Universal API should call it with. A credential for the property itself is '
                        + 'issued automatically if it has none.'}
                    confirmLabel={saving ? 'Saving…' : 'Add access'}
                    onConfirm={save}
                    onCancel={() => setAdding(null)}
                >
                    <label className="uat-field">
                        <span>Backend system</span>
                        <select
                            className="ppw-select"
                            value={marketplaceId}
                            onChange={(e) => { setMarketplaceId(e.target.value); setCredential(null); }}
                        >
                            <option value="">Choose a system…</option>
                            {systems.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                    </label>

                    {marketplaceId && (
                        <CredentialSelector
                            marketplaceId={Number(marketplaceId)}
                            hotelId={adding.hotelId}
                            accountId={accountId}
                            value={credential}
                            onChange={setCredential}
                            canEdit
                            title="Credential"
                            subtitle="The credential the Universal API calls this system with — one of yours, at hotel, chain or partner level."
                        />
                    )}

                    <label className="uat-field">
                        <span>Property code at that system <em>optional</em></span>
                        <input
                            type="text"
                            className="form-input"
                            value={hotelIdOverride}
                            onChange={(e) => setHotelIdOverride(e.target.value)}
                            placeholder="Leave blank to use the code on the credential"
                        />
                    </label>

                    <label className="uat-field">
                        <span>Name for this access <em>optional</em></span>
                        <input
                            type="text"
                            className="form-input"
                            maxLength={255}
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            placeholder="Only needed when a property has more than one"
                        />
                    </label>
                </ConfirmModal>
            )}

            {confirmRemove && (
                <ConfirmModal
                    title="Remove Universal API access"
                    message={`Stop routing ${confirmRemove.hotelname || 'this property'} to `
                        + `${confirmRemove.system_name}? The x-hotelid `
                        + `${confirmRemove.uapi_hotel_id} is retired with it, and anything still sending `
                        + 'it stops being routed immediately.'}
                    confirmLabel="Remove"
                    danger
                    onConfirm={remove}
                    onCancel={() => setConfirmRemove(null)}
                />
            )}
        </div>
    );
};

export default UapiAccessTab;
